<template>
  <div
    v-if="show"
    class="ddt-backdrop"
    @click.self="$emit('update:show', false)"
  >
    <div class="ddt-modal">
      <button
        class="icons-button ddt-modal__close"
        type="button"
        @click="$emit('update:show', false)"
      >
        <i class="fa-solid fa-xmark"></i>
      </button>

      <h3 class="ddt-modal__title">Genera DDT</h3>
      <p class="ddt-modal__subtitle" v-if="clientName">
        Committente: <strong>{{ clientName }}</strong>
      </p>

      <!-- Selezione causale -->
      <div class="ddt-field">
        <label class="ddt-label">Causale *</label>
        <div class="ddt-causale-cards">
          <button
            v-for="opt in CAUSALI"
            :key="opt.value"
            type="button"
            class="ddt-causale-card"
            :class="{ selected: causale === opt.value }"
            @click="causale = opt.value"
          >
            <i :class="opt.icon"></i>
            <span>{{ opt.label }}</span>
            <small v-if="opt.sub" class="ddt-causale-sub">{{ opt.sub }}</small>
          </button>
        </div>
      </div>

      <!-- Selezione operatori -->
      <div class="ddt-operators-row">
        <div class="ddt-field">
          <label class="ddt-label">Magazziniere</label>
          <select v-model="magazziniereId" class="ddt-select">
            <option :value="null">— Seleziona —</option>
            <option
              v-for="op in warehouseOperators"
              :key="op.id"
              :value="op.id"
            >
              {{ op.operatorName || op.email }}
            </option>
          </select>
        </div>
        <div class="ddt-field">
          <label class="ddt-label">Operatore</label>
          <select v-model="operatoreId" class="ddt-select">
            <option :value="null">— Seleziona —</option>
            <option v-for="op in adminOperators" :key="op.id" :value="op.id">
              {{ op.operatorName || op.email }}
            </option>
          </select>
        </div>
      </div>

      <!-- Selezione articoli -->
      <div class="ddt-field">
        <label class="ddt-label">
          Articoli
          <span class="ddt-items-count"
            >({{ selectedCount }} / {{ deliverableCount }} selezionati)</span
          >
        </label>
        <div class="ddt-items-preview">
          <table class="ddt-preview-table">
            <thead>
              <tr>
                <th class="ddt-col-check">
                  <input
                    type="checkbox"
                    :checked="allSelected"
                    :indeterminate.prop="someSelected && !allSelected"
                    @change="toggleAll"
                  />
                </th>
                <th>Descrizione</th>
                <th class="ddt-col-qty">Quantità</th>
                <th class="ddt-col-uom">UdM</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(it, i) in selectionItems"
                :key="i"
                :class="{ 'ddt-row-disabled': !it.selected }"
              >
                <td class="ddt-col-check">
                  <input
                    type="checkbox"
                    v-model="it.selected"
                    :disabled="Number(it.quantity) <= 0"
                    :title="
                      Number(it.quantity) <= 0
                        ? 'Articolo già consegnato per intero'
                        : ''
                    "
                  />
                </td>
                <td>
                  <div class="ddt-item-name">{{ it.description }}</div>
                  <span
                    v-if="it.deliveryLabel"
                    class="ddt-delivery-badge"
                    :class="{
                      'ddt-delivery-badge--full': it.deliveryFull,
                    }"
                  >
                    {{ it.deliveryLabel }}
                  </span>
                </td>
                <td class="ddt-col-qty">
                  <input
                    v-if="it.selected"
                    type="number"
                    class="ddt-qty-input"
                    v-model.number="it.editableQty"
                    min="1"
                    :max="it.quantity"
                    step="1"
                    @input="clampQty(it)"
                  />
                  <span v-else class="ddt-qty-muted">{{ it.quantity }}</span>
                </td>
                <td class="ddt-col-uom">{{ it.uom }}</td>
              </tr>
              <tr v-if="selectionItems.length === 0">
                <td colspan="4" style="text-align: center; color: #888">
                  Nessun articolo.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Note per il cliente -->
      <div class="ddt-field">
        <label class="ddt-label" for="ddt-notes">Note per il cliente</label>
        <textarea
          id="ddt-notes"
          v-model="notes"
          class="ddt-textarea"
          rows="3"
          maxlength="2000"
          placeholder="Inserisci eventuali specifiche per la consegna"
        ></textarea>
      </div>

      <div class="ddt-modal__actions">
        <button
          class="btn btn-secondary"
          type="button"
          @click="$emit('update:show', false)"
        >
          Annulla
        </button>
        <button
          class="btn btn-primary"
          type="button"
          :disabled="!causale || selectedCount === 0 || busy"
          @click="generate"
        >
          <span v-if="busy">Generazione…</span>
          <span v-else
            ><i class="fa-solid fa-file-arrow-down"></i> Genera DDT</span
          >
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axiosInstance from "@/axiosInstance";
import { mapActions, mapGetters } from "vuex/dist/vuex.cjs.js";
import { generateDdtPdf } from "@/utilities/generateDdtPdf";
import { trackEvent, EVENTS } from "@/utilities/analytics";

const CAUSALI = [
  {
    value: "consegna",
    label: "Consegna",
    sub: "al cliente",
    icon: "fa-solid fa-truck",
  },
  {
    value: "ritiro",
    label: "Ritiro",
    sub: "in sede",
    icon: "fa-solid fa-box-open",
  },
];

export default {
  name: "DdtModal",
  props: {
    show: { type: Boolean, required: true },
    items: { type: Array, default: () => [] },
    quoteId: { type: Number, default: null },
    clientId: { type: Number, default: null },
    clientName: { type: String, default: null },
    deferred: { type: Boolean, default: false },
  },
  emits: ["update:show", "generated", "queued"],
  data() {
    return {
      CAUSALI,
      causale: null,
      magazziniereId: null,
      operatoreId: null,
      notes: "",
      busy: false,
      selectionItems: [],
    };
  },
  computed: {
    ...mapGetters({ operators: "operators/list" }),
    warehouseOperators() {
      return (this.operators || []).filter((op) => op.isAdmin === false);
    },
    adminOperators() {
      return (this.operators || []).filter((op) => op.isAdmin === true);
    },
    // Solo gli articoli con quantità residua da consegnare (esclusi i già
    // consegnati per intero): sono gli unici selezionabili/conteggiati.
    deliverableItems() {
      return this.selectionItems.filter((it) => Number(it.quantity) > 0);
    },
    deliverableCount() {
      return this.deliverableItems.length;
    },
    selectedCount() {
      return this.deliverableItems.filter((it) => it.selected).length;
    },
    allSelected() {
      return (
        this.deliverableCount > 0 &&
        this.deliverableItems.every((it) => it.selected)
      );
    },
    someSelected() {
      return this.deliverableItems.some((it) => it.selected);
    },
    // true se questo DDT copre l'intero residuo di TUTTI gli articoli non
    // ancora consegnati per intero (nessun residuo rimane dopo la generazione).
    isLastDelivery() {
      if (this.deliverableCount === 0) return false;
      return this.selectionItems.every((it) => {
        const residual = Number(it.quantity) || 0;
        if (residual <= 0) return true; // già consegnato per intero
        if (!it.selected) return false; // rimarrebbe del residuo
        const qty = Math.min(Math.floor(Number(it.editableQty) || 0), residual);
        return qty >= residual;
      });
    },
  },
  watch: {
    show(val) {
      if (val) {
        this.fetchOperators();
        this.initSelection();
      } else {
        this.causale = null;
        this.magazziniereId = null;
        this.operatoreId = null;
        this.notes = "";
      }
    },
    items() {
      this.initSelection();
    },
  },
  methods: {
    ...mapActions(["fetchOperators"]),
    clampQty(item) {
      const val = Math.floor(Number(item.editableQty));
      if (!val || val < 1) {
        item.editableQty = 1;
      } else if (val > item.quantity) {
        item.editableQty = Math.floor(item.quantity);
      } else {
        item.editableQty = val;
      }
    },
    initSelection() {
      this.selectionItems = this.items.map((it) => {
        const residual = Number(it.quantity) || 0;
        const delivered = Number(it.deliveredQty) || 0;
        // Stato consegna: interamente consegnato (residuo 0) o parziale (X/Y).
        let deliveryLabel = null;
        let deliveryFull = false;
        if (delivered > 0) {
          if (residual <= 0) {
            deliveryLabel = "Interamente consegnato";
            deliveryFull = true;
          } else {
            deliveryLabel = `Consegnato ${delivered}/${delivered + residual}`;
          }
        }
        return {
          ...it,
          // Gli articoli già consegnati per intero (quantità residua 0)
          // partono deselezionati e non riselezionabili.
          selected: residual > 0,
          editableQty: Math.floor(it.quantity),
          deliveryLabel,
          deliveryFull,
        };
      });
    },
    toggleAll(e) {
      const val = e.target.checked;
      // Non tocca gli articoli già consegnati per intero (non selezionabili).
      this.selectionItems.forEach((it) => {
        if (Number(it.quantity) > 0) it.selected = val;
      });
    },
    async generate() {
      const activeItems = this.selectionItems
        .filter((it) => it.selected)
        .map((it) => ({
          itemId: it.itemId || null,
          description: it.description,
          quantity: Math.min(it.editableQty, it.quantity),
          uom: it.uom,
        }));
      if (!this.causale || activeItems.length === 0) return;

      const notes = this.notes.trim() || null;

      if (this.deferred) {
        this.$emit("queued", {
          causale: this.causale,
          magazziniereId: this.magazziniereId,
          operatoreId: this.operatoreId,
          notes,
          items: activeItems,
        });
        this.$toast.info(
          "DDT in coda. Verrà generato al salvataggio del preventivo."
        );
        this.$emit("update:show", false);
        return;
      }

      this.busy = true;
      try {
        const { data: ddt } = await axiosInstance.post("/ddt", {
          causale: this.causale,
          quoteId: this.quoteId,
          clientId: this.clientId,
          magazziniereId: this.magazziniereId,
          operatoreId: this.operatoreId,
          notes,
          items: activeItems,
        });

        const blob = generateDdtPdf(ddt);
        // Nome tecnico della funzione ed esito: nessun dato del documento.
        trackEvent(EVENTS.FEATURE_USED, {
          feature: "ddt_pdf",
          action: "export",
          outcome: "success",
        });
        const isDev =
          process.env.NODE_ENV === "development" ||
          import.meta?.env?.MODE === "development" ||
          process.env?.VUE_APP_NODE_ENV === "development";
        const withSuffixBeforeExt = (name, suffix) => {
          if (!suffix) return name;
          const dot = name.lastIndexOf(".");
          return dot <= 0
            ? `${name}.${suffix}`
            : `${name.slice(0, dot)}.${suffix}${name.slice(dot)}`;
        };
        const baseName = ddt.quoteId
          ? ddt.causale === "consegna"
            ? `DDT_${ddt.quoteId}_CONSEGNA_${ddt.sequence}.pdf`
            : `DDT_${ddt.quoteId}_${ddt.causale.toUpperCase()}.pdf`
          : `DDT_N_${ddt.id}.pdf`;
        const filename = withSuffixBeforeExt(baseName, isDev ? "dev" : "");

        // Carica su Minio
        const formData = new FormData();
        formData.append(
          "document",
          new File([blob], filename, { type: "application/pdf" })
        );
        await axiosInstance.post(`/upload/ddts/${ddt.id}/documents`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        // Scarica il PDF localmente
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);

        this.$toast.success(`DDT N° ${ddt.id} generato e salvato.`);
        this.$emit("generated", { ddt, isLastDelivery: this.isLastDelivery });
        this.$emit("update:show", false);
      } catch (e) {
        this.$toast.error(
          e?.response?.data?.message || "Errore nella generazione del DDT."
        );
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>

<style scoped>
.ddt-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
}
.ddt-modal {
  background: #fff;
  border-radius: 8px;
  padding: 28px 32px;
  min-width: 380px;
  max-width: 560px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
}

@media (max-width: 768px) {
  .ddt-modal {
    width: 85%;
    padding: 28px 12px;
  }
}
.ddt-modal__close {
  position: absolute;
  top: 12px;
  right: 12px;
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 8px;
  cursor: pointer;
}

.ddt-modal__close i {
  color: black;
}

.ddt-modal__close:hover {
  background: #ccc;
}

.ddt-modal__title {
  margin: 0 0 4px;
  font-size: 1.1rem;
}
.ddt-modal__subtitle {
  margin: 0 0 16px;
  color: #666;
  font-size: 0.9rem;
}
.ddt-field {
  margin-bottom: 18px;
}
.ddt-label {
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 8px;
}
.ddt-causale-cards {
  display: flex;
  gap: 10px;
}
.ddt-causale-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 12px 8px;
  border: 2px solid #ddd;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  transition: border-color 0.15s, background 0.15s;
}
.ddt-causale-card.selected {
  border-color: #1a1a1a;
  background: #f5f5f5;
}
.ddt-causale-card i {
  font-size: 18px;
}
.ddt-causale-sub {
  font-size: 11px;
  font-weight: 400;
  color: #888;
}
.ddt-items-preview {
  max-height: 220px;
  overflow-y: auto;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-bottom: 20px;
}
.ddt-preview-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.ddt-preview-table th,
.ddt-preview-table td {
  padding: 7px 10px;
  text-align: left;
  border-bottom: 1px solid #eee;
}
.ddt-preview-table thead th {
  background: #f8f8f8;
  font-weight: 600;
}
.ddt-operators-row {
  display: flex;
  gap: 14px;
  margin-bottom: 18px;
}
.ddt-operators-row .ddt-field {
  flex: 1;
  margin-bottom: 0;
}
.ddt-select {
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 13px;
}
.ddt-textarea {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 13px;
  font-family: inherit;
  resize: vertical;
  box-sizing: border-box;
}
.ddt-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
.ddt-items-count {
  font-weight: 400;
  color: #888;
  font-size: 0.8rem;
  margin-left: 4px;
}
.ddt-col-check {
  width: 32px;
  text-align: center !important;
  padding-left: 8px !important;
  padding-right: 8px !important;
}
.ddt-col-qty {
  width: 90px;
}
.ddt-col-uom {
  width: 52px;
}
.ddt-row-disabled td {
  opacity: 0.4;
}
.ddt-qty-input {
  width: 76px;
  padding: 2px 6px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 13px;
  text-align: right;
}
.ddt-qty-muted {
  color: #aaa;
  font-size: 13px;
}
.ddt-item-name {
  line-height: 1.3;
}
.ddt-delivery-badge {
  display: inline-block;
  margin-top: 4px;
  padding: 1px 7px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.5;
  white-space: nowrap;
  color: #92400e;
  background: #fef3c7;
  border: 1px solid #fde68a;
}
.ddt-delivery-badge--full {
  color: #166534;
  background: #dcfce7;
  border-color: #bbf7d0;
}
</style>
